import { call, all, put, takeEvery, select } from 'redux-saga/effects';
import * as actionTypes from './actionTypes';
import axios from 'axios';
import DateTimeUtils from "../shared/utils/DateTimeUtils";
import StringUtils from "../shared/utils/StringUtils";
import { getEntityListCall } from '../entities/sagas';
import { I18n } from 'react-redux-i18n';
import { getUserPageRefresh } from '../auth/sagas';

const controllerUrl = '/interfaces/';

const searchInterfacesCall = searchFields => new Promise((resolve, reject) => {
        axios.get(controllerUrl + 'search', {
            params: {
                ...searchFields
            }
        }).then(
            response => resolve(response.data),
            error => reject(error)
        );
});

const listInterfacesCall = searchFields => new Promise((resolve, reject) => {
        axios.get(controllerUrl + 'list', {
            params: {
                ...searchFields
            }
        }).then(
            response => resolve(response.data),
            error => reject(error)
        );
});

const getInterfaceCall = (id) => new Promise((resolve, reject) => {
    axios.get(controllerUrl + id).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const createInterfaceCall = interfaceData => new Promise((resolve, reject) => {
    axios.post(controllerUrl, interfaceData).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const editInterfaceCall = (interfaceId, interfaceData) => new Promise((resolve, reject) => {
    axios.put(controllerUrl + interfaceId, interfaceData).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const deleteInterfaceCall = interfaceId => new Promise((resolve, reject) => {
    axios.delete(controllerUrl + interfaceId).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const getUnitTypesCall = () => new Promise((resolve, reject) => {
    axios.get(controllerUrl + 'unitTypes/').then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const getCategoriesCall = () => new Promise((resolve, reject) => {
    axios.get(controllerUrl + 'categories/').then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const getAssociatedEntitiesCall = (interfaceId) => new Promise((resolve, reject) => {
    axios.get(controllerUrl + interfaceId + '/entityInterfaces/entities/').then(
        response => resolve(response.data),
        error => reject(error)
    );
});

function* searchInterfacesRequest({ searchFields }) { //Warning: String values are normalized for presentation in tables
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const searchResults = yield call(searchInterfacesCall, searchFields);

        const searchResultsWithDate = {
            ...searchResults,
            content: searchResults.content.map(
                interfaceInstance => ({
                    ...interfaceInstance,
                    lastCommunicationTimestamp: interfaceInstance.lastCommunicationTimestamp ? DateTimeUtils.getDateTimeFromUnix(interfaceInstance.lastCommunicationTimestamp) : null,
                    category: {value: StringUtils.stringNormalize(interfaceInstance.category.value)},
                    historyConfiguration: StringUtils.stringNormalize(interfaceInstance.historyConfiguration),
                    dataType: StringUtils.stringNormalize(interfaceInstance.dataType)
                })
            )
        };

        yield put({
            type: actionTypes.INTERFACE_SEARCH_SUCCESS,
            payload: searchResultsWithDate
        });
    } catch (e) {
        yield put({
            type: actionTypes.INTERFACE_SEARCH_FAILED,
            payload: e
        });
    }
}

function* listInterfacesRequest({ searchFields }) { //Warning: String values are normalized for presentation in tables
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const searchResults = yield call(() => listInterfacesCall(searchFields));

        // const searchResultsWithDate = {
        //     ...searchResults,
        //     content: searchResults.content.map(
        //         interfaceInstance => ({
        //             ...interfaceInstance,
        //             lastCommunication: interfaceInstance.lastCommunicationTimestamp ? DateTimeUtils.getDateTimeFromUnix(interfaceInstance.lastCommunicationTimestamp) : null,
        //             category: {value: StringUtils.stringNormalize(interfaceInstance.category.value)},
        //             historyConfiguration: StringUtils.stringNormalize(interfaceInstance.historyConfiguration),
        //             dataType: StringUtils.stringNormalize(interfaceInstance.dataType)
        //         })
        //     )
        // };

        yield put({
            type: actionTypes.GET_INTERFACE_LIST_SUCCESS,
            payload: searchResults.map(terminalInterface => ({ value: terminalInterface.id, label: terminalInterface.name }))
        });
    } catch (e) {
        yield put({
            type: actionTypes.GET_INTERFACE_LIST_FAILED,
            payload: e
        });
    }
}

function* getEntityLastReadings({ entityId }) { //Warning: String values are normalized for presentation in tables
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const searchResults = yield call(() => listInterfacesCall({entityId: [entityId]}));

        const filteredResults = searchResults.reduce((object, interfaceInstance) => {
                if(interfaceInstance.lastReading && interfaceInstance.category && interfaceInstance.category.value){
                    object[interfaceInstance.category.value] = interfaceInstance.lastReading;
                    return object
                } else {
                    return {}
                }
            }, {});

        yield put({
            type: actionTypes.GET_ENTITY_LAST_READINGS_SUCCESS,
            payload: filteredResults
        });
    } catch (e) {
        yield put({
            type: actionTypes.GET_ENTITY_LAST_READINGS_FAILED,
            payload: e
        });
    }
}

function* getInterfaceRequest({ interfaceId }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const interfaceInstance = yield call(getInterfaceCall, interfaceId);

        interfaceInstance.timezone = interfaceInstance.timeZone;
        interfaceInstance.lastCommunication = interfaceInstance.lastCommunicationTimestamp ? DateTimeUtils.getDateTimeFromUnix(interfaceInstance.lastCommunicationTimestamp) : null;
        interfaceInstance.category = interfaceInstance.category.value;
        interfaceInstance.unitType = interfaceInstance.unitType.value;

        yield put({
            type: actionTypes.GET_INTERFACE_SUCCESS,
            payload: interfaceInstance
        });
    } catch (e) {
        yield put({
            type: actionTypes.GET_INTERFACE_FAILED,
        });
    }
}

function* createInterfaceRequest({ interfaceData }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        interfaceData.timeZone = interfaceData.timezone;

        yield call(createInterfaceCall, interfaceData);

        yield put({
            type: actionTypes.CREATE_INTERFACE_SUCCESS
        })
    } catch (e) {
        yield put({
            type: actionTypes.CREATE_INTERFACE_FAILED
        })
    }
}

function* editInterfaceRequest({ interfaceId, interfaceData }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        interfaceData.timeZone = interfaceData.timezone;

        yield call(editInterfaceCall, interfaceId, interfaceData);

        yield put({
            type: actionTypes.EDIT_INTERFACE_SUCCESS
        })
    } catch (e) {
        yield put({
            type: actionTypes.EDIT_INTERFACE_FAILED
        })
    }
}

function* deleteInterfaceRequest({ interfaceId }) {
    try {
        yield call(deleteInterfaceCall, interfaceId);

        yield put({
            type: actionTypes.DELETE_INTERFACE_SUCCESS
        })
    } catch (e) {
        yield put({
            type: actionTypes.DELETE_INTERFACE_FAILED
        })
    }
}

function* getUnitTypesRequest() {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const unitTypeList = yield call(getUnitTypesCall);

        const unitTypeListWithLabel = unitTypeList.map(
            unitTypeInstance => ({
                ...unitTypeInstance,
                label: I18n.t('interfaces.unitTypes.' + unitTypeInstance.value)
            })
        );

        yield put({
            type: actionTypes.GET_UNIT_TYPE_LIST_SUCCESS,
            payload: unitTypeListWithLabel
        })
    } catch (e) {
        yield put({
            type: actionTypes.GET_UNIT_TYPE_LIST_FAILED
        })
    }
}

function* getCategoriesRequest() {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const categoryList = yield call(getCategoriesCall);

        const categoryListWithLabel = categoryList.map(
            categoryInstance => ({
                ...categoryInstance,
                label: I18n.t('interfaces.interfaceCategories.' + categoryInstance.value)
            })
        );

        yield put({
            type: actionTypes.GET_CATEGORY_LIST_SUCCESS,
            payload: categoryListWithLabel
        })
    } catch (e) {
        yield put({
            type: actionTypes.GET_CATEGORY_LIST_FAILED
        })
    }
}

function* getTerminalsRelatedEntities({ terminalsParentEntityId }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const availableEntityList = yield call(getEntityListCall, { parentId: terminalsParentEntityId });

        yield put({
            type: actionTypes.GET_TERMINALS_RELATED_ENTITIES_SUCCESS,
            payload: availableEntityList.map(e => ({
                label: e.name,
                value: e.id
            }))
        })
    } catch (e) {
        yield put({
            type: actionTypes.GET_TERMINALS_RELATED_ENTITIES_FAILED,
            payload: e
        })
    }
}

function* getAssociatedEntitiesRequest({ interfaceId }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const associatedEntitiesList = yield call(getAssociatedEntitiesCall, interfaceId);

        const associatedEntitiesReducedList = associatedEntitiesList.map(
            entityInstance => ({
                name: entityInstance.name,
                type: entityInstance.type
            })
        );

        yield put({
            type: actionTypes.GET_ASSOCIATED_ENTITIES_LIST_SUCCESS,
            payload: associatedEntitiesReducedList
        })
    } catch (e) {
        yield put({
            type: actionTypes.GET_ASSOCIATED_ENTITIES_LIST_FAILED,
            payload: e
        })
    }
}

function* loadInterfacesDetailsRequest({ interfaceIds }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const interfacesDetailsArray = yield all(interfaceIds.map(id => call(getInterfaceCall, id)));

        let interfacesDetails = interfacesDetailsArray.reduce(
            (acc, terminalInterface) => ({
                ...acc,
                [terminalInterface.id]: terminalInterface
            }),
            {}
        );

        yield put({
            type: actionTypes.LOAD_INTERFACES_DETAILS_SUCCESS,
            payload: interfacesDetails
        })
    } catch (e) {
        yield put({
            error: interfaceIds,
            type: actionTypes.LOAD_INTERFACES_DETAILS_FAILED
        });
    }
}

const interfaceSagas = [
    takeEvery(actionTypes.INTERFACE_SEARCH_REQUEST, searchInterfacesRequest),
    takeEvery(actionTypes.GET_INTERFACE_LIST_REQUEST, listInterfacesRequest),
    takeEvery(actionTypes.GET_INTERFACE_REQUEST, getInterfaceRequest),
    takeEvery(actionTypes.CREATE_INTERFACE_REQUEST, createInterfaceRequest),
    takeEvery(actionTypes.EDIT_INTERFACE_REQUEST, editInterfaceRequest),
    takeEvery(actionTypes.DELETE_INTERFACE_REQUEST, deleteInterfaceRequest),
    takeEvery(actionTypes.GET_UNIT_TYPE_LIST_REQUEST, getUnitTypesRequest),
    takeEvery(actionTypes.GET_CATEGORY_LIST_REQUEST, getCategoriesRequest),
    takeEvery(actionTypes.GET_TERMINALS_RELATED_ENTITIES_REQUEST, getTerminalsRelatedEntities),
    takeEvery(actionTypes.GET_ASSOCIATED_ENTITIES_LIST_REQUEST, getAssociatedEntitiesRequest),
    takeEvery(actionTypes.LOAD_INTERFACES_DETAILS_REQUEST, loadInterfacesDetailsRequest),
    takeEvery(actionTypes.GET_ENTITY_LAST_READINGS_REQUEST, getEntityLastReadings)
];

export {
    interfaceSagas,
    getInterfaceCall,
    listInterfacesCall
};